import {KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Button} from '@ui/atoms/button';
import {CancelIcon, ChevronDownIcon} from '@ui/atoms/icons';
import {Modal} from '@ui/molecules/modal';
import {
  TemplateBrowserCard,
  TemplateCardDivider,
  TemplateCategoryLabel,
  TemplateCategoryRow,
  TemplateCategorySelect,
  TemplateDialog,
  TemplateDialogClose,
  TemplateDialogContent,
  TemplateDialogFooter,
  TemplateDialogHeader,
  TemplateDialogPrimaryButton,
  TemplateDialogState,
  TemplateDialogTitle,
  TemplateListFade,
  TemplateListViewport,
  TemplateQuestionList,
  TemplateQuestionRow,
  TemplateSelectionCount,
} from './content-step.styles';
import {getScrollIndicators} from './content-step.utils';
import {TemplateQuestionOption} from './types';

interface QuestionTemplateBrowserProps {
  open: boolean;
  options: TemplateQuestionOption[];
  loading?: boolean;
  error?: string | null;
  alreadyAddedIds: string[];
  onRetry: () => void;
  onClose: () => void;
  onConfirm: (questions: TemplateQuestionOption[]) => void;
}

export const QuestionTemplateBrowser = ({
  open,
  options,
  loading = false,
  error,
  alreadyAddedIds,
  onRetry,
  onClose,
  onConfirm,
}: QuestionTemplateBrowserProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [indicators, setIndicators] = useState({
    canScrollUp: false,
    canScrollDown: false,
  });

  const categories = useMemo(
    () => Array.from(new Set(options.map((option) => option.category))),
    [options],
  );

  const visibleQuestions = useMemo(
    () => options.filter((option) => option.category === selectedCategory),
    [options, selectedCategory],
  );

  useEffect(() => {
    if (!open) return;

    setSelectedIds([]);
    const generalCategory = categories.find(
      (category) => category.toLowerCase() === 'general questions',
    );
    setSelectedCategory(generalCategory || categories[0] || '');
    window.requestAnimationFrame(() => dialogRef.current?.focus());
  }, [categories, open]);

  const updateIndicators = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    setIndicators(
      getScrollIndicators({
        scrollTop: el.scrollTop,
        scrollHeight: el.scrollHeight,
        clientHeight: el.clientHeight,
      }),
    );
  }, []);

  useEffect(() => {
    updateIndicators();
  }, [updateIndicators, visibleQuestions, selectedCategory, open]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => updateIndicators());
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateIndicators]);

  const handleClose = () => {
    setSelectedIds([]);
    onClose();
  };

  const handleDialogKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      handleClose();
      return;
    }

    if (event.key !== 'Tab' || !dialogRef.current) return;
    const focusable = Array.from(
      dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const alreadyAdded = new Set(alreadyAddedIds);
  const selectedQuestions = selectedIds
    .map((id) => options.find((option) => option.id === id))
    .filter(Boolean) as TemplateQuestionOption[];

  const toggleQuestion = (question: TemplateQuestionOption) => {
    if (alreadyAdded.has(question.id)) return;
    setSelectedIds((current) =>
      current.includes(question.id)
        ? current.filter((id) => id !== question.id)
        : [...current, question.id],
    );
  };

  const confirmSelection = () => {
    if (!selectedQuestions.length) return;
    onConfirm(selectedQuestions);
    setSelectedIds([]);
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <TemplateDialog
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="question-template-dialog-title"
        tabIndex={-1}
        onKeyDown={handleDialogKeyDown}>
        <TemplateDialogHeader>
          <TemplateDialogTitle id="question-template-dialog-title">
            Question library
          </TemplateDialogTitle>
          <TemplateDialogClose type="button" onClick={handleClose}>
            <CancelIcon /> Close
          </TemplateDialogClose>
        </TemplateDialogHeader>

        <TemplateDialogContent>
          <TemplateBrowserCard>
            {!loading && !error && categories.length > 0 && (
              <TemplateCategoryRow>
                <TemplateCategoryLabel>Choose a category</TemplateCategoryLabel>
                <span className="relative inline-block">
                  <TemplateCategorySelect
                    aria-label="Question category"
                    value={selectedCategory}
                    onChange={(event) => setSelectedCategory(event.target.value)}>
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </TemplateCategorySelect>
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2">
                    <ChevronDownIcon color="#242424" />
                  </span>
                </span>
              </TemplateCategoryRow>
            )}

            {loading ? (
              <TemplateDialogState role="status">
                Loading question library…
              </TemplateDialogState>
            ) : error ? (
              <TemplateDialogState role="alert">
                <span>{error}</span>
                <Button kind="secondary" type="button" onClick={onRetry}>
                  Retry
                </Button>
              </TemplateDialogState>
            ) : categories.length === 0 ? (
              <TemplateDialogState>
                No questions are available in the library.
              </TemplateDialogState>
            ) : visibleQuestions.length === 0 ? (
              <TemplateDialogState>
                No questions are available in this category.
              </TemplateDialogState>
            ) : (
              <>
                <TemplateCardDivider />
                <TemplateListViewport>
                  {indicators.canScrollUp && (
                    <TemplateListFade
                      position="top"
                      aria-hidden="true"
                      data-testid="scroll-fade-top"
                    />
                  )}
                  <TemplateQuestionList
                    ref={listRef}
                    onScroll={updateIndicators}
                    role="listbox"
                    aria-label={`${selectedCategory} questions`}
                    aria-multiselectable="true">
                    {visibleQuestions.map((question) => {
                      const selected = selectedIds.includes(question.id);
                      const disabled = alreadyAdded.has(question.id);
                      return (
                        <TemplateQuestionRow
                          key={question.id}
                          type="button"
                          role="option"
                          aria-selected={selected}
                          aria-label={
                            disabled ? `${question.label}, already added` : question.label
                          }
                          selected={selected}
                          disabled={disabled}
                          onClick={() => toggleQuestion(question)}>
                          {question.label}
                        </TemplateQuestionRow>
                      );
                    })}
                  </TemplateQuestionList>
                  {indicators.canScrollDown && (
                    <TemplateListFade
                      position="bottom"
                      aria-hidden="true"
                      data-testid="scroll-fade-bottom"
                    />
                  )}
                </TemplateListViewport>
              </>
            )}
          </TemplateBrowserCard>
        </TemplateDialogContent>

        <TemplateDialogFooter>
          <TemplateSelectionCount aria-live="polite">
            {selectedIds.length}{' '}
            {selectedIds.length === 1 ? 'question selected' : 'questions selected'}
          </TemplateSelectionCount>
          <TemplateDialogPrimaryButton
            type="button"
            disabled={selectedQuestions.length === 0}
            aria-label={`Add ${selectedQuestions.length} selected questions`}
            onClick={confirmSelection}>
            Add questions
          </TemplateDialogPrimaryButton>
        </TemplateDialogFooter>
      </TemplateDialog>
    </Modal>
  );
};
